import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  SetRole, SetAllRoles, RemoveRole, SetRoleDataLoaded,
  SetSelectedRole, SetAssignedSites, SetAllSites, SetAssignedSiteCount,
  SetSelectedPermissionSite, SetAssignedUsers, SetAllUsers,
  SetAssignedZones, SetAllZones, ClearRoleSession
} from './roles.actions';
import { IRole } from './roles.model';
import { IRoleSite } from '../sites.state/sites.model';
import { IRoleUser } from '../user.state/users.model';
import { IZonePermission } from 'src/app/role-management/services/roles.service';

export class RolesStateModel {
  roles: IRole[] = [];
  canidates: IRole[] = [];
  dataLoaded: boolean = false;

  // Role session state
  selectedRole: IRole | null = null;
  assignedSites: IRoleSite[] = [];
  allSites: IRoleSite[] = [];
  assignedSiteCount: number = 0;
  selectedPermissionSite: IRoleSite | null = null;
  assignedUsers: IRoleUser[] = [];
  allUsers: IRoleUser[] = [];
  assignedZones: IZonePermission[] = [];
  allZones: IZonePermission[] = [];
}

@State<RolesStateModel>({
  name: 'StateRoles',
  defaults: new RolesStateModel()
})

@Injectable()
export class RolesState {
  constructor() {}

  // ── Existing selectors ────────────────────────────────────────────────────

  @Selector()
  static getRoles(state: RolesStateModel): IRole[] {
    return state.roles;
  }

  @Selector()
  static getRolesDataLoaded(state: RolesStateModel): boolean {
    return state.dataLoaded;
  }

  // ── New selectors ─────────────────────────────────────────────────────────

  @Selector()
  static getSelectedRole(state: RolesStateModel): IRole | null {
    return state.selectedRole;
  }

  @Selector()
  static getAssignedSites(state: RolesStateModel): IRoleSite[] {
    return state.assignedSites;
  }

  @Selector()
  static getAllSites(state: RolesStateModel): IRoleSite[] {
    return state.allSites;
  }

  @Selector()
  static getAssignedSiteCount(state: RolesStateModel): number {
    return state.assignedSiteCount;
  }

  @Selector()
  static getSelectedPermissionSite(state: RolesStateModel): IRoleSite | null {
    return state.selectedPermissionSite;
  }

  @Selector()
  static getAssignedUsers(state: RolesStateModel): IRoleUser[] {
    return state.assignedUsers;
  }

  @Selector()
  static getAllUsers(state: RolesStateModel): IRoleUser[] {
    return state.allUsers;
  }

  @Selector()
  static getAssignedZones(state: RolesStateModel): IZonePermission[] {
    return state.assignedZones;
  }

  @Selector()
  static getAllZones(state: RolesStateModel): IZonePermission[] {
    return state.allZones;
  }

  // ── Existing action handlers ──────────────────────────────────────────────

  @Action(SetRole)
  setRole({getState, patchState}: StateContext<RolesStateModel>, { role, patchNewState }: SetRole): void {
    const state = getState();

    const existingRoleIndex: number = state.roles.findIndex((inUrole: IRole) => inUrole.id === role.id);
    if (existingRoleIndex >= 0) {
      state.roles[existingRoleIndex] = role;
    } else {
      state.roles.push(role);
    }

    if (patchNewState) {
      patchState({ roles: [...state.roles] });
    }
  }

  @Action(SetAllRoles)
  setAllRoles(stateContext: StateContext<RolesStateModel>, { roles }: SetAllRoles): void {
    const state = stateContext.getState();
    roles.forEach((zone: IRole) => {
      this.setRole(stateContext, new SetRole(zone, false));
    });
    stateContext.patchState({ roles: [...state.roles] });
  }

  @Action(RemoveRole)
  removeRole({getState, patchState}: StateContext<RolesStateModel>, { roleId }: RemoveRole): void {
    patchState({
      roles: getState().roles.filter((user: IRole) => user.id !== roleId)
    });
  }

  @Action(SetRoleDataLoaded)
  setRoleDataLoaded({ patchState }: StateContext<RolesStateModel>, { loading }: SetRoleDataLoaded): void {
    patchState({ dataLoaded: loading });
  }

  // ── New action handlers ───────────────────────────────────────────────────

  @Action(SetSelectedRole)
  setSelectedRole({ patchState }: StateContext<RolesStateModel>, { role }: SetSelectedRole): void {
    patchState({ selectedRole: role });
  }

  @Action(SetAssignedSites)
  setAssignedSites({ patchState }: StateContext<RolesStateModel>, { sites }: SetAssignedSites): void {
    patchState({ assignedSites: sites });
  }

  @Action(SetAllSites)
  setAllSites({ patchState }: StateContext<RolesStateModel>, { sites }: SetAllSites): void {
    patchState({ allSites: sites });
  }

  @Action(SetAssignedSiteCount)
  setAssignedSiteCount({ patchState }: StateContext<RolesStateModel>, { count }: SetAssignedSiteCount): void {
    patchState({ assignedSiteCount: count });
  }

  @Action(SetSelectedPermissionSite)
  setSelectedPermissionSite({ patchState }: StateContext<RolesStateModel>, { site }: SetSelectedPermissionSite): void {
    patchState({ selectedPermissionSite: site });
  }

  @Action(SetAssignedUsers)
  setAssignedUsers({ patchState }: StateContext<RolesStateModel>, { users }: SetAssignedUsers): void {
    patchState({ assignedUsers: users });
  }

  @Action(SetAllUsers)
  setAllUsers({ patchState }: StateContext<RolesStateModel>, { users }: SetAllUsers): void {
    patchState({ allUsers: users });
  }

  @Action(SetAssignedZones)
  setAssignedZones({ patchState }: StateContext<RolesStateModel>, { zones }: SetAssignedZones): void {
    patchState({ assignedZones: zones });
  }

  @Action(SetAllZones)
  setAllZones({ patchState }: StateContext<RolesStateModel>, { zones }: SetAllZones): void {
    patchState({ allZones: zones });
  }

  @Action(ClearRoleSession)
  clearRoleSession({ patchState }: StateContext<RolesStateModel>): void {
    patchState({
      selectedRole: null,
      assignedSites: [],
      allSites: [],
      assignedSiteCount: 0,
      selectedPermissionSite: null,
      assignedUsers: [],
      allUsers: [],
      assignedZones: [],
      allZones: []
    });
  }
}
